import i18n from '@/libs/i18n'
import store from '@/store'
import useEntityCrud from '@/composable/useEntityCrud'
import useEntityHelper from '@/composable/useEntityHelper'
import useErrorManager from '@/composable/useErrorManager'
import { ref, computed } from '@vue/composition-api'

export default function useArticlesList() {
  const ENDPOINT = 'mom-experience'
  const MODULE_NAME = 'momExperiences'
  const errorManager = useErrorManager()
  const { getAlt, createTagIdArray } = useEntityHelper()
  function convertResource(resource, selectedLang) {
    return {
      ...resource,
      title: resource.title[selectedLang],
      keywords: resource.keywords[selectedLang],
      description: resource.description[selectedLang],
      content: resource.content[selectedLang],
      lang: selectedLang,
      momExperienceThumbnail: resource.momExperienceThumbnailFileId[selectedLang],
      momExperienceVideo: resource.momExperienceVideoFileId[selectedLang],
      approver: resource.approverId ? {
        _id: resource.approverId._id,
        slug: resource.approverId.slug,
        firstName: resource.approverId.firstName[selectedLang],
        lastName: resource.approverId.lastName[selectedLang],
        managerImageFileId: resource.approverId.managerImageFileId,
      } : null,
      tags: resource.tagIds && resource.tagIds.length ? resource.tagIds.filter(itemEntry => itemEntry && itemEntry.language === selectedLang) : null,
      relatedArticles: resource.relatedArticleIds,
      momExperienceThumbnailAlt: getAlt('momExperienceThumbnailFileId', resource, selectedLang),
      momExperienceVideoAlt: getAlt('momExperienceVideoFileId', resource, selectedLang),
      momExperienceLink: { name: `pages-${MODULE_NAME}-edit`, params: { id: resource._id, lang: selectedLang } },
    }
  }

  const entity = useEntityCrud(MODULE_NAME, ENDPOINT, convertResource)
  const { itemLoading, item, form } = entity

  // Table Handlers
  const tableColumns = computed(() => [
    {
      key: 'title', label: i18n.t('general.title'), type: 'textImage', textKey: 'title', imageKey: 'momExperienceThumbnail', linkKey: 'momExperienceLink',
    },
    { key: 'status', label: i18n.t('articles.status'), icon: 'ClipboardIcon' },
    {
      key: 'approver', label: i18n.t('general.approver'), type: 'manager', icon: 'UserCheckIcon',
    },
    {
      key: 'createdAt', label: i18n.t('general.createdAt'), type: 'dateTime', sortable: true,
    },
    {
      key: 'updatedAt', label: i18n.t('general.updatedAt'), type: 'dateTime', sortable: true,
    },
    { key: 'actions', label: i18n.t('general.actions'), sortable: false },
  ])

  const articleOptions = ref([])
  const statusOptions = ref(['Published', 'Draft'])
  const approverOptions = ref([])
  const isUniqueSlug = ref(true)

  const fetchArticles = (lang = 'fa') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: { lang },
        endpoint: 'articles',
      })
      .then(response => {
        articleOptions.value = response.data.data.map(itemEntry2 => ({ _id: itemEntry2._id, title: itemEntry2.title, articleThumbnailFileId: itemEntry2.articleThumbnailFileId }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const fetchApprovers = (lang = 'fa', search = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: { lang, search },
        endpoint: 'managers',
      })
      .then(response => {
        approverOptions.value = response.data.data.map(itemEntry3 => ({
          _id: itemEntry3._id,
          firstName: itemEntry3.firstName[lang],
          lastName: itemEntry3.lastName[lang],
          managerImageFileId: itemEntry3.managerImageFileId,
        }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const checkSlugIsUnique = (lang = 'fa', slug) => {
    if (slug === '' || (item.value && item.value.slug === form.value.slug)) {
      isUniqueSlug.value = true
      return
    }
    itemLoading.value = true
    store
      .dispatch('crud/fetchItem',
        {
          data: { id: slug },
          queryParams: { lang },
          endpoint: 'articles/slug',
        })
      .then(response => {
        isUniqueSlug.value = response.data.isUnique
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  return {
    ...entity,
    errorManager,
    approverOptions,
    articleOptions,
    checkSlugIsUnique,
    convertResource,
    createTagIdArray,
    fetchApprovers,
    fetchArticles,
    isUniqueSlug,
    statusOptions,
    tableColumns,
  }
}
